/**
 * CLIKKER LOGIN
 * Handles login, signup, Google authentication and password reset.
 * Version: 2.2.1
 *
 * Required:
 * - clikker-firebase.js v2.5.1+
 * - Matching auth page markup
 *
 * Default destination:
 * - /pages/clikker-profile
 */

(function () {
  "use strict";

  var DEFAULT_DESTINATION = "/pages/clikker-profile";
  var ALLOWED_MODES = ["login", "signup", "forgot"];

  document.body.classList.add("clikker-auth-active");

  var loginForm = document.getElementById("clikkerLoginForm");
  var signupForm = document.getElementById("clikkerSignupForm");
  var forgotForm = document.getElementById("clikkerForgotForm");

  var loginTab = document.getElementById("clikkerLoginTab");
  var signupTab = document.getElementById("clikkerSignupTab");

  var forgotOpen = document.getElementById("clikkerForgotOpen");
  var forgotBack = document.getElementById("clikkerForgotBack");

  var loginSubmit = document.getElementById("clikkerLoginSubmit");
  var signupSubmit = document.getElementById("clikkerSignupSubmit");
  var forgotSubmit = document.getElementById("clikkerForgotSubmit");

  var messageBox = document.getElementById("clikkerAuthMessage");
  var signupTerms = document.getElementById("clikkerSignupTerms");
  var googleButtons = Array.prototype.slice.call(
    document.querySelectorAll("[data-google-login]")
  );

  var requiredElements = {
    loginForm: loginForm,
    signupForm: signupForm,
    forgotForm: forgotForm,
    loginTab: loginTab,
    signupTab: signupTab,
    forgotOpen: forgotOpen,
    forgotBack: forgotBack,
    loginSubmit: loginSubmit,
    signupSubmit: signupSubmit,
    forgotSubmit: forgotSubmit,
    messageBox: messageBox,
    signupTerms: signupTerms
  };

  var missingElements = Object.keys(requiredElements).filter(function (key) {
    return !requiredElements[key];
  });

  if (missingElements.length) {
    console.error(
      "[Clikker Login] Required page elements are missing:",
      missingElements
    );
    return;
  }

  function getMode() {
    var mode = new URL(window.location.href).searchParams.get("mode");
    return ALLOWED_MODES.indexOf(mode) >= 0 ? mode : "login";
  }

  function getActiveForm(mode) {
    if (mode === "signup") return signupForm;
    if (mode === "forgot") return forgotForm;
    return loginForm;
  }

  function setMode(mode, updateUrl, focusInput) {
    var selectedMode =
      ALLOWED_MODES.indexOf(mode) >= 0 ? mode : "login";

    loginForm.hidden = selectedMode !== "login";
    signupForm.hidden = selectedMode !== "signup";
    forgotForm.hidden = selectedMode !== "forgot";

    loginTab.classList.toggle("is-active", selectedMode === "login");
    signupTab.classList.toggle("is-active", selectedMode === "signup");

    loginTab.setAttribute(
      "aria-selected",
      selectedMode === "login" ? "true" : "false"
    );

    signupTab.setAttribute(
      "aria-selected",
      selectedMode === "signup" ? "true" : "false"
    );

    clearMessage();

    if (updateUrl) {
      var url = new URL(window.location.href);
      url.searchParams.set("mode", selectedMode);
      window.history.replaceState({}, "", url.toString());
    }

    document.title =
      selectedMode === "signup"
        ? "Create Your Creator Store | Clikker"
        : selectedMode === "forgot"
        ? "Reset Creator Password | Clikker"
        : "Creator Login | Clikker";

    if (focusInput === true) {
      var firstInput = getActiveForm(selectedMode).querySelector("input");

      if (firstInput) {
        window.setTimeout(function () {
          firstInput.focus();
        }, 0);
      }
    }
  }

  function showMessage(text, type) {
    var selectedType = type === "success" ? "success" : "error";

    messageBox.hidden = false;
    messageBox.className =
      "clikker-auth-message clikker-auth-message--" + selectedType;
    messageBox.textContent = String(text || "");

    messageBox.setAttribute(
      "role",
      selectedType === "error" ? "alert" : "status"
    );
    messageBox.setAttribute("aria-live", "polite");

    messageBox.scrollIntoView({
      behavior: "smooth",
      block: "nearest"
    });
  }

  function clearMessage() {
    messageBox.hidden = true;
    messageBox.textContent = "";
    messageBox.className = "";
    messageBox.removeAttribute("role");
  }

  function setBusy(button, busy, busyText) {
    if (!button) return;

    if (busy) {
      if (!button.dataset.originalText) {
        button.dataset.originalText = button.textContent;
      }

      button.disabled = true;
      button.setAttribute("aria-busy", "true");
      button.textContent = busyText || "Please wait…";
      return;
    }

    button.disabled = false;
    button.removeAttribute("aria-busy");
    button.textContent =
      button.dataset.originalText || button.textContent;

    delete button.dataset.originalText;
  }

  function destination() {
    var url = new URL(window.location.href);
    var requested =
      url.searchParams.get("next") ||
      url.searchParams.get("return") ||
      "";

    if (!requested) return DEFAULT_DESTINATION;

    /*
     * URLSearchParams.get() already decodes the query value.
     * Only allow same-site relative paths and reject protocol-relative URLs.
     */
    if (
      requested.charAt(0) !== "/" ||
      requested.indexOf("//") === 0 ||
      requested.indexOf("\\") >= 0
    ) {
      return DEFAULT_DESTINATION;
    }

    return requested;
  }

  async function ensureClikkerReady() {
    if (!window.Clikker) {
      throw new Error(
        "Clikker Firebase could not load. Check clikker-firebase.js."
      );
    }

    if (typeof window.Clikker.waitUntilReady === "function") {
      await window.Clikker.waitUntilReady();
    } else if (
      window.Clikker.readyPromise &&
      typeof window.Clikker.readyPromise.then === "function"
    ) {
      await window.Clikker.readyPromise;
    }

    if (
      typeof window.Clikker.loginWithEmail !== "function" ||
      typeof window.Clikker.registerWithEmail !== "function" ||
      typeof window.Clikker.loginWithGoogle !== "function" ||
      typeof window.Clikker.sendPasswordReset !== "function"
    ) {
      throw new Error(
        "Clikker authentication functions are unavailable."
      );
    }
  }

  function friendlyError(error) {
    var code = error && error.code ? String(error.code) : "";

    var messages = {
      "auth/email-already-in-use":
        "This email is already registered. Please login instead.",
      "auth/invalid-email":
        "Please enter a valid email address.",
      "auth/weak-password":
        "Password must contain at least 6 characters.",
      "auth/invalid-credential":
        "The email or password is incorrect.",
      "auth/invalid-login-credentials":
        "The email or password is incorrect.",
      "auth/user-not-found":
        "No account was found for this email.",
      "auth/wrong-password":
        "The password is incorrect.",
      "auth/user-disabled":
        "This account has been disabled. Please contact Clikker support.",
      "auth/too-many-requests":
        "Too many attempts. Please wait a few minutes and try again.",
      "auth/network-request-failed":
        "Network error. Check your internet connection and try again.",
      "auth/operation-not-allowed":
        "This sign-in method is not enabled.",
      "auth/popup-blocked":
        "Google sign-in popup was blocked. Allow popups and try again.",
      "auth/popup-closed-by-user":
        "Google sign-in was cancelled.",
      "auth/cancelled-popup-request":
        "Another Google sign-in request is already open.",
      "auth/unauthorized-domain":
        "This domain is not authorized in Firebase Authentication.",
      "permission-denied":
        "Your account is authenticated, but Clikker could not access your profile."
    };

    return (
      messages[code] ||
      (error && error.message) ||
      "Authentication failed. Please try again."
    );
  }

  function normalizeEmail(value) {
    return String(value || "").trim().toLowerCase();
  }

  function restoreGoogleButtons() {
    googleButtons.forEach(function (button) {
      setBusy(button, false);
      button.disabled = false;
    });
  }

  function redirectAfterAuthentication() {
    var target = destination();
    var redirected = false;

    try {
      window.location.assign(target);
      redirected = true;
    } catch (error) {
      console.error("[Clikker Login] Redirect failed:", error);
    }

    /*
     * If navigation is delayed or blocked, restore controls so the page
     * does not remain permanently disabled.
     */
    window.setTimeout(function () {
      if (!redirected || window.location.pathname.indexOf("/clikker-login") >= 0) {
        setBusy(loginSubmit, false);
        setBusy(signupSubmit, false);
        restoreGoogleButtons();
      }
    }, 2500);
  }

  function requireGoogleSignupConsent(button) {
    var mode = String(button.dataset.googleMode || "login").toLowerCase();

    if (mode !== "signup") return true;

    if (!signupTerms.checked) {
      setMode("signup", true, false);
      showMessage(
        "Please accept the Clikker Terms and Creator Policy before continuing with Google.",
        "error"
      );
      signupTerms.focus();
      return false;
    }

    return true;
  }

  loginTab.addEventListener("click", function (event) {
    event.preventDefault();
    setMode("login", true, true);
  });

  signupTab.addEventListener("click", function (event) {
    event.preventDefault();
    setMode("signup", true, true);
  });

  forgotOpen.addEventListener("click", function (event) {
    event.preventDefault();

    var loginEmail = loginForm.querySelector('[name="email"]');
    var forgotEmail = forgotForm.querySelector('[name="email"]');

    if (loginEmail && forgotEmail && loginEmail.value) {
      forgotEmail.value = loginEmail.value;
    }

    setMode("forgot", true, true);
  });

  forgotBack.addEventListener("click", function (event) {
    event.preventDefault();
    setMode("login", true, true);
  });

  loginForm.addEventListener("submit", async function (event) {
    event.preventDefault();
    event.stopPropagation();

    if (!loginForm.checkValidity()) {
      loginForm.reportValidity();
      return;
    }

    var data = new FormData(loginForm);

    try {
      clearMessage();
      setBusy(loginSubmit, true, "Logging in…");

      await ensureClikkerReady();

      await window.Clikker.loginWithEmail(
        normalizeEmail(data.get("email")),
        String(data.get("password") || "")
      );

      redirectAfterAuthentication();
    } catch (error) {
      console.error("[Clikker Login] Email login failed:", error);
      showMessage(friendlyError(error), "error");
      setBusy(loginSubmit, false);
    }
  });

  signupForm.addEventListener("submit", async function (event) {
    event.preventDefault();
    event.stopPropagation();

    if (!signupForm.checkValidity()) {
      signupForm.reportValidity();
      return;
    }

    var data = new FormData(signupForm);

    try {
      clearMessage();
      setBusy(signupSubmit, true, "Creating account…");

      await ensureClikkerReady();

      await window.Clikker.registerWithEmail({
        name: String(data.get("name") || "").trim(),
        email: normalizeEmail(data.get("email")),
        password: String(data.get("password") || "")
      });

      redirectAfterAuthentication();
    } catch (error) {
      console.error("[Clikker Login] Signup failed:", error);
      showMessage(friendlyError(error), "error");
      setBusy(signupSubmit, false);
    }
  });

  forgotForm.addEventListener("submit", async function (event) {
    event.preventDefault();
    event.stopPropagation();

    if (!forgotForm.checkValidity()) {
      forgotForm.reportValidity();
      return;
    }

    var data = new FormData(forgotForm);
    var email = normalizeEmail(data.get("email"));

    try {
      clearMessage();
      setBusy(forgotSubmit, true, "Sending…");

      await ensureClikkerReady();
      await window.Clikker.sendPasswordReset(email);

      var loginEmail = loginForm.querySelector('[name="email"]');

      if (loginEmail) {
        loginEmail.value = email;
      }

      setBusy(forgotSubmit, false);
      setMode("login", true, false);

      showMessage(
        "Password reset email sent. Check your inbox and spam folder, then login with your new password.",
        "success"
      );

      if (loginEmail) {
        window.setTimeout(function () {
          loginEmail.focus();
        }, 0);
      }
    } catch (error) {
      console.error("[Clikker Login] Password reset failed:", error);
      showMessage(friendlyError(error), "error");
      setBusy(forgotSubmit, false);
    }
  });

  googleButtons.forEach(function (button) {
    button.addEventListener("click", async function (event) {
      event.preventDefault();
      event.stopPropagation();

      if (!requireGoogleSignupConsent(button)) {
        return;
      }

      /*
       * Disable every Google button so the user cannot open multiple popups.
       */
      googleButtons.forEach(function (googleButton) {
        if (googleButton !== button) {
          googleButton.disabled = true;
        }
      });

      try {
        clearMessage();

        var googleMode = String(button.dataset.googleMode || "login").toLowerCase();
        setBusy(
          button,
          true,
          googleMode === "signup"
            ? "Creating account with Google…"
            : "Connecting to Google…"
        );

        await ensureClikkerReady();

        var googleUser = await window.Clikker.loginWithGoogle({
          mode: googleMode,
          googleMode: googleMode,
          termsAccepted:
            googleMode === "signup" && signupTerms.checked === true,
          creatorPolicyAccepted:
            googleMode === "signup" && signupTerms.checked === true,
          consentSource: "google_" + googleMode
        });

        if (!googleUser || !googleUser.uid) {
          throw new Error(
            "Google authentication completed without a valid Firebase user."
          );
        }

        /*
         * Firebase v2.5.1 repairs missing Google displayName/photoURL and
         * synchronizes them to Firestore before returning. Refresh the
         * shared Clikker context once more when available so the destination
         * page receives the newest account data immediately.
         */
        if (
          window.Clikker &&
          typeof window.Clikker.refreshCurrentContext === "function"
        ) {
          try {
            await window.Clikker.refreshCurrentContext();
          } catch (contextError) {
            console.warn(
              "[Clikker Login] Google profile synced, but context refresh was delayed:",
              contextError
            );
          }
        }

        console.info("[Clikker Login] Google authentication complete", {
          uid: googleUser.uid,
          displayName: googleUser.displayName || "",
          email: googleUser.email || "",
          hasPhoto: Boolean(googleUser.photoURL)
        });

        redirectAfterAuthentication();
      } catch (error) {
        console.error("[Clikker Login] Google login failed:", error);
        showMessage(friendlyError(error), "error");
        restoreGoogleButtons();
      }
    });
  });

  window.addEventListener("popstate", function () {
    setMode(getMode(), false, false);
  });

  setMode(getMode(), false, false);
})();
